import { getDatadogApmConfig, type DatadogApmConfig, type DatadogTags } from './config';
import { resolveCohort } from './cohort';
import { homepageDocumentPath, normalizeRouteTemplate } from './route';

type SpanContext = {
  toTraceId?: () => string;
  toSpanId?: () => string;
};

type Span = {
  context: () => SpanContext;
  setTag: (name: string, value: string) => void;
};

type Tracer = {
  init: (configuration: DatadogApmConfig & { logInjection: boolean }) => void;
  inject?: (context: SpanContext, format: string, carrier: { dd?: Record<string, string> }) => void;
  scope: () => { active: () => Span | null };
  trace?: <T>(name: string, options: { tags?: Record<string, string> }, callback: () => T) => T;
  use: (name: string, configuration: { hooks: { request: (span: Span, request: unknown) => void } }) => void;
};

type RuntimeRequire = (moduleId: string) => Tracer;

let activeTracer: Tracer | undefined;
let initialized = false;

// The runtime shim (apps/web/eb-web-dd-trace.cjs, copied to the working
// directory in the image) re-exports dd-trace from the isolated
// apps/web/runtime/dd-trace/node_modules tree where the Dockerfile installs it.
// In the container process.cwd() is the
// standalone server directory (Next's generated server.js does
// `process.chdir(__dirname)`), which is where the shim is copied — so requiring
// the shim by absolute path resolves the library deterministically without
// adding dd-trace to the standalone trace graph. Locally the shim may be
// absent; the bare 'dd-trace' fallback covers a developer install, and any
// failure yields undefined so tracing simply stays off.
function loadTracer(): Tracer | undefined {
  const shimPath = `${process.cwd()}/eb-web-dd-trace.cjs`;
  const getBuiltinModule = (
    process as typeof process & { getBuiltinModule?: (id: string) => { createRequire: (path: string) => RuntimeRequire } }
  ).getBuiltinModule;
  const createRequire = getBuiltinModule?.('module')?.createRequire;
  if (!createRequire) {
    return undefined;
  }
  const runtimeRequire = createRequire(shimPath);
  for (const moduleId of [shimPath, 'dd-trace']) {
    try {
      return runtimeRequire(moduleId);
    } catch {
      // Try the next resolution strategy.
    }
  }
  return undefined;
}

function requestResourceName(request: unknown): string | undefined {
  if (!request || typeof request !== 'object') {
    return undefined;
  }

  const candidate = request as {
    method?: string;
    path?: string;
    url?: string | { href?: string };
  };
  const method = candidate.method?.toUpperCase() || 'GET';
  const rawUrl = typeof candidate.url === 'string' ? candidate.url : candidate.url?.href;
  const path = rawUrl ?? candidate.path;

  if (!path) {
    return undefined;
  }

  try {
    const parsed = new URL(path);
    return `${method} ${parsed.protocol}//${parsed.host}${normalizeRouteTemplate(parsed.pathname)}`;
  } catch {
    return `${method} ${normalizeRouteTemplate(path)}`;
  }
}

// The homepage fold's ramp monitors need the APM server span to carry the same
// bounded dimensions the structured logs do. `surface` keys on the folded
// homepage mount (`/home` or `/home/api/**`); `cohort` is read from the
// edge-stamped `x-eb-origin-bucket` header and validated to old|new|unavailable
// (never echoed). Both are low-cardinality, so they are safe span tags.
function spanDimensions(request: unknown): Record<string, string> {
  if (!request || typeof request !== 'object') return {};
  const candidate = request as {
    path?: string;
    url?: string | { href?: string };
    headers?: Record<string, unknown>;
  };
  const rawUrl = typeof candidate.url === 'string' ? candidate.url : candidate.url?.href;
  const path = rawUrl ?? candidate.path ?? '';
  let pathname: string;
  try {
    pathname = new URL(path).pathname;
  } catch {
    pathname = path.split(/[?#]/, 1)[0] || path;
  }
  pathname = homepageDocumentPath(pathname);
  const isHomepage =
    pathname === '/home' ||
    pathname.startsWith('/home/') ||
    pathname === '/api/home' ||
    pathname.startsWith('/api/home/');
  if (!isHomepage) {
    return {};
  }
  const headers = candidate.headers;
  const bucket = headers?.['x-eb-origin-bucket'] ?? headers?.['X-Eb-Origin-Bucket'];
  return { surface: 'homepage_webapp', cohort: resolveCohort(bucket) };
}

function normalizeHttpRequest(span: Span, request: unknown): void {
  const resourceName = requestResourceName(request);
  if (resourceName) {
    span.setTag('resource.name', resourceName);
  }
  for (const [name, value] of Object.entries(spanDimensions(request))) {
    span.setTag(name, value);
  }
}

export function initializeDatadogTracing(
  environment: Record<string, string | undefined> = process.env,
  loader: () => Tracer | undefined = loadTracer,
): boolean {
  if (initialized) {
    return true;
  }

  const config = getDatadogApmConfig(environment);
  if (!config) {
    return false;
  }

  const tracer = loader();
  if (!tracer) {
    return false;
  }

  try {
    if (!(globalThis as Record<symbol, unknown>)[Symbol.for('eb-web.datadog-tracing-preloaded')]) {
      tracer.init({ ...config, logInjection: false });
    }
    tracer.use('http', { hooks: { request: normalizeHttpRequest } });
    tracer.use('fetch', { hooks: { request: normalizeHttpRequest } });
    activeTracer = tracer;
    initialized = true;
    return true;
  } catch {
    return false;
  }
}

export function trace<T>(fn: () => Promise<T>, name: string, tags?: Record<string, string>): Promise<T> {
  const tracer = activeTracer;
  if (!tracer?.trace) {
    return fn();
  }

  let result: Promise<T> | undefined;
  try {
    return Promise.resolve(tracer.trace(name, { tags }, () => {
      result = fn();
      return result;
    }));
  } catch {
    return result ?? fn();
  }
}

function safeId(value: string | undefined): string | undefined {
  return value && (/^\d{1,39}$/.test(value) || /^[a-f\d]{16}(?:[a-f\d]{16})?$/i.test(value))
    ? value
    : undefined;
}

export type DatadogTraceContext = {
  'dd.trace_id'?: string;
  'dd.span_id'?: string;
  'dd.service'?: string;
  'dd.env'?: string;
  'dd.version'?: string;
};

export function getDatadogTraceContext(
  environment: Record<string, string | undefined> = process.env,
): DatadogTraceContext {
  const tags: DatadogTags | undefined = getDatadogApmConfig(environment);
  const tracer = activeTracer;
  if (!tags || !tracer) {
    return {};
  }

  try {
    const context = tracer.scope().active()?.context();
    if (!context) {
      return {};
    }

    const carrier: { dd?: Record<string, string> } = {};
    tracer.inject?.(context, 'log', carrier);
    const traceId = safeId(carrier.dd?.trace_id ?? context.toTraceId?.());
    const spanId = safeId(carrier.dd?.span_id ?? context.toSpanId?.());

    if (!traceId || !spanId) {
      return {};
    }

    return {
      'dd.trace_id': traceId,
      'dd.span_id': spanId,
      'dd.service': tags.service,
      'dd.env': tags.env,
      'dd.version': tags.version,
    };
  } catch {
    return {};
  }
}

export function resetDatadogTracingForTests(): void {
  activeTracer = undefined;
  initialized = false;
}
